// Shop-template composition.
//
// Reads `scene.template.knobs` and applies the chosen preset materials +
// lighting recipe to a freshly-loaded shell. Spec: docs/SHOP_TEMPLATE_preset-spec.md.
//
// Opt-in: when `scene.template` is absent, this module is a no-op and the
// runtime falls back to raw GLB materials + `_applyShellInteriorLighting`.

import * as THREE from "../../vendor/three.module.js";
import { resolveEnvironment, resolveZonePlan, resolveShadows, HUB_FALLBACK_HDRI } from "./_lighting-recipe.js";   // M7 Chunk 3 / WS4 (ASK-080 §A) — HUB_FALLBACK_HDRI: the bundled IBL fallback (ASK-099)
import { resolveAssetKeyUrl, resolveEnvAssetUrl, envAssetCandidates } from "./_asset-url-resolver.js";
// ASK-080 Track-1 pristine gate. Imported LAZILY, at its `?diagnose=1` call site — see
// `emitPristineDiagnose`. It was a static import, so its whole report vocabulary (nine row
// definitions and their words) shipped in every customer's initial JS to serve a surface reached
// only by typing a query parameter. ASK-129 §studio-gate widened row 6 and the ceiling caught it at
// 1392.2/1392 KB — paid for the way §vacant-sign paid, by moving the code rather than the ceiling.
import { resolveShellSlot, SHELL_SLOTS } from "./_shell-slot-resolver.js";   // node → surface slot (retires SHELL_PATTERNS)
import { resolveSlotBinding, applyInstanceOverride, resolveKnobBinding, resolveMapSet, resolveTilingMeters, foldInstanceMaterialKnobs, foldKindDefaultKnobs, fixtureKindPresetCategory } from "./_material-binding.js";
import { buildMaterialFromLibrary, applyBoxProjectedUVs } from "./_material-apply.js";   // DECISION-053 Slice-2: shared build + per-face box projection
import { resolveShadowModel, resolveShadowIntensity, ambientRebalanceForShadow } from "./_shadow-fit.js";   // ASK-084: A/B model + shadowIntensity knob
import { foldShopKnobs } from "./_shop-knob-wire.js";   // ASK-129 F1: the per-shop wire shape, both directions, one table

// `?diagnose=1` → log which legacy fallbacks fire for the active shop (pristine gate).
function diagnoseEnabled() {
	try { return typeof location !== "undefined" && /[?&]diagnose=1\b/.test(location.search || ""); }
	catch { return false; }
}

// Per-template defaults. v1: comic-shop + vinyl-store.
const DEFAULTS = {
	"comic-shop": {
		// Elevated-classic palette for Kapow's showcase — mirrored from
		// forge-templates PR #42 (Content, ASK-058) into the live SPA path
		// pre-Forge-cutover. SPA reads these DEFAULTS, not the manifest, until
		// cutover; no Forge re-bake, so this sidesteps ASK-053. Was:
		// wallMaterial concrete-bare / floorMaterial concrete-polished /
		// counterMaterial concrete-bar.
		wallMaterial:     "painted-brick",
		floorMaterial:    "wood-floorboards",
		ceilingTreatment: "exposed-industrial",
		lightingRecipe:   "industrial-loft",
		fixtureMaterial:  "wood-warm",
		counterMaterial:  "wood-stained-dark",
		signMode:         "typographic-shopname",
		accentColor:      "#f8e6c0",  // warm white default — brand glow on sign + (future) accent lighting
		brightness:       1.0,
		shadowIntensity:  2.5,   // ASK-084: contact-caster intensity → visible grounding (real-surface receiving)
	},
	"vinyl-store": {
		wallMaterial:     "concrete-bare",
		floorMaterial:    "concrete-polished",
		ceilingTreatment: "painted-flat",
		lightingRecipe:   "gallery-spotlights",
		fixtureMaterial:  "wood-warm",
		counterMaterial:  "wood-stained-dark",
		signMode:         "typographic-shopname",
		accentColor:      "#ff2a8a",  // Nocturn-style hot-pink neon
		brightness:       1.0,
		shadowIntensity:  2.5,
	},
};

// Brightness slider bounds — guardrails so a runaway slider can't push the
// scene to unrenderable black or blown-out white. UI clamps to this too.
const BRIGHTNESS_MIN = 0.3;
const BRIGHTNESS_MAX = 2.5;
function clampBrightness(v) {
	const n = Number(v);
	if (!Number.isFinite(n)) return 1.0;
	return Math.max(BRIGHTNESS_MIN, Math.min(BRIGHTNESS_MAX, n));
}

// Per-shop knob keys baked at `template.knobs.shop` (DECISION-008 scope namespace, primitive shape
// per the /knobs/shop endpoint caps — string|number|boolean|null only). The flat split between
// material id (`wallMaterial: "painted-brick"`) and tint (`wallTint: "#1d1820"`) exists because
// primitives can't nest.
//
// ASK-129 §E2E102 F1 — the table and BOTH directions moved to `_shop-knob-wire.js`. They used to
// live here, one-way: this file could unfold the wire, and nothing anywhere could fold INTO it, so
// the Studio's writer silently dropped every object-valued knob a merchant chose. A translation
// with one direction implemented is not a translation.

// Shell node → surface slot classification moved to _shell-slot-resolver.js (resolveShellSlot) —
// a data table resolved by SPECIFICITY, retiring the order-sensitive SHELL_PATTERNS if/else chain
// (counter-before-fixture) this const used to be (DECISION-053 Slice-2).


// Public entry. Returns `{ composed: bool, lightingHandled: bool }`.
//   composed === true     → template path took over; caller should not re-apply
//                           raw materials.
//   lightingHandled       → true when a lighting preset was applied. Caller
//                           must skip `_applyShellInteriorLighting`.
export async function composeTemplate({ shellRoot, scene, stage, templatePresets, projectAssets, commerce, shopTitle, templateCatalogue, materialLibrary, templateSurfaceMaterials }) {
	const tmpl = scene?.template;
	if (!tmpl?.id) return { composed: false, lightingHandled: false };

	// templateId resolves from scene.template.id (the scene package) — NOT the shop entry
	// (which carries no templateId). This is how templatePresets already resolves, so the
	// parallel templateSurfaceMaterials binding rides the same path for free (answer to
	// Content's open question, DECISION-053 Slice-2 / ASK-083).
	const templateId = tmpl.id;
	const defaults   = DEFAULTS[templateId] || {};
	const knobs      = foldShopKnobs(tmpl.knobs || {}, tmpl.knobs?.shop);

	const presetsForTemplate = templatePresets?.[templateId];
	if (!presetsForTemplate) {
		console.warn(`[template] no preset library found for template '${templateId}'; skipping composition.`);
		return { composed: false, lightingHandled: false };
	}

	const resolved = {
		wall:    resolvePreset(presetsForTemplate, "wall-material",     knobs.wallMaterial,     defaults.wallMaterial),
		floor:   resolvePreset(presetsForTemplate, "floor-material",    knobs.floorMaterial,    defaults.floorMaterial),
		ceiling: resolvePreset(presetsForTemplate, "ceiling-treatment", knobs.ceilingTreatment, defaults.ceilingTreatment),
		fixture: resolvePreset(presetsForTemplate, "fixture-material",  knobs.fixtureMaterial,  defaults.fixtureMaterial),
		counter: resolvePreset(presetsForTemplate, "counter-material",  knobs.counterMaterial,  defaults.counterMaterial),
		light:   resolvePreset(presetsForTemplate, "lighting-recipe",   knobs.lightingRecipe,   defaults.lightingRecipe),
	};

	// Material-depth (ASK-085 layered resolution): each shell slot's binding is
	//   static table (templateSurfaceMaterials, the no-knob default)
	//     → KNOB layer (merchant Material-Instance preset + tint chip, resolveKnobBinding)
	//       → per-instance override (scene instanceMaterialOverrides)
	// merged field-by-field via applyInstanceOverride (later layers win). A tint-only knob layers
	// onto the static material (the tint-chip rewire); a knob preset with no materialId (legacy
	// procedural / pre-emit) is a no-op so statics stand until Content's Material-Instance emit.
	// Built up-front (await) so the traverse assigns synchronously. An UNBOUND slot keeps its
	// authored GLB material (no placeholder — Step 6).
	shellRoot.updateMatrixWorld(true);
	const renderer = stage?.renderer || null;
	const anisotropy = Math.min(8, renderer?.capabilities?.getMaxAnisotropy?.() || 8);
	const shellOverrides = scene?.instanceMaterialOverrides || null;
	// ASK-085 part-2 — stash the resolved fixture-FINISH preset (the FIXTURES dropdown pick) so the
	// composer + the fixture-material rebinds resolve byKind from it; re-stashed on every recompose,
	// which is what makes a dropdown change repaint fixtures live.
	if (stage) stage._fixtureFinishPreset = resolved.fixture || null;
	// Amendment 3 — per-KIND defaults: fold fixtureMaterial.<kind> (flat wire) → kind→presetId,
	// resolve each to its preset entry, stash for the 4-layer fixture resolution
	// (instance > kind-default > global > static). Re-stashed per recompose = live.
	if (stage) {
		const kindDefaults = foldKindDefaultKnobs({ ...(tmpl.knobs?.shop || {}), ...(tmpl.knobs || {}) }) || {};   // authored wins
		const byKindPreset = {};
		for (const [k, presetId] of Object.entries(kindDefaults)) {
			const entry = presetsForTemplate?.[fixtureKindPresetCategory(k)]?.[presetId];
			if (entry) byKindPreset[k] = entry;
		}
		stage._fixtureKindDefaultPresets = byKindPreset;
	}
	// ASK-086 — fold the flat per-instance grammar (material.<anchor>.<slot>.<prop>) from BOTH the
	// authored knobs and the /knobs/shop wire map into per-anchor cells; stash for the instance
	// layer (instance > finish > static). Re-stashed per recompose = live repaint on panel writes.
	if (stage) {
		const placed = (stage._fixtureMaterialRebinds || []).map((r) => r.anchorName).filter(Boolean);
		stage._instanceMaterialKnobs = foldInstanceMaterialKnobs(
			{ ...(tmpl.knobs?.shop || {}), ...(tmpl.knobs || {}) },   // authored (session edits) WIN over baked wire keys
			placed.length ? placed : null,   // first compose: registry empty → no filter (unconsumed keys are inert)
		);
	}
	// slot → [resolved-preset key, knob key] (door has no knob today — static/instance only)
	const KNOB_BY_SLOT = { wall: ["wall", "wallMaterial"], floor: ["floor", "floorMaterial"], ceiling: ["ceiling", "ceilingTreatment"], counter: ["counter", "counterMaterial"] };
	const boundBySlot = {};
	// ASK-142 §12 — the shop's light (its env HDRI) is requested NOW, beside the wall materials, not
	// after them: measured on NET=phone it started only once both material sets had landed (18.9 s)
	// and the loader waits for it. `applyShopEnvironment` dedups, so the recipe's own call below joins
	// this request instead of issuing a second.
	if (resolved.light?.lighting && typeof stage?.applyShopEnvironment === "function") {
		const req = envRequestFor(resolved.light, stage?._templatesSha || null);
		if (req.envUrls.length || req.envFallbackUrl) void stage.applyShopEnvironment(req.envUrls, req.lodUrls, req.envFallbackUrl);
	}
	// …and every slot's material set loads AT ONCE: they were awaited one slot at a time, so the
	// plaster set (9.0 → 13.7 s) and the concrete set (13.7 → 18.8 s) queued behind each other.
	const slotBuilds = [];
	for (const slot of SHELL_SLOTS) {
		let binding = resolveSlotBinding(templateSurfaceMaterials, templateId, slot);
		const kb = KNOB_BY_SLOT[slot];
		if (kb) binding = applyInstanceOverride(binding, resolveKnobBinding(resolved[kb[0]], knobs[kb[1]]?.tint ?? null));
		binding = applyInstanceOverride(binding, shellOverrides?.[slot]);   // per-instance wins over knob
		if (!binding?.materialId) continue;
		const mapSet = resolveMapSet(materialLibrary, binding.materialId, stage?.renderTier || null);
		if (!mapSet || !resolveAssetKeyUrl(mapSet.baseColor)) continue;   // no CDN / library entry → procedural
		slotBuilds.push(buildMaterialFromLibrary(mapSet, binding, { renderer, anisotropy }).then((material) => {
			if (material) boundBySlot[slot] = { material, metresPerTile: resolveTilingMeters(mapSet, binding.tilingScale), aoMap: !!material.aoMap };
		}, (err) => {
			console.warn(`[template] library material '${binding.materialId}' for slot '${slot}' failed; procedural fallback:`, err?.message || err);
		}));
	}
	await Promise.all(slotBuilds);

	let swapped = 0, mapped = 0;
	shellRoot.traverse((node) => {
		if (!node.isMesh) return;
		const slot = resolveShellSlot(node.name || "");   // order-independent (retired SHELL_PATTERNS)
		if (!slot) return;
		const bound = boundBySlot[slot];
		if (bound) {
			// Real library material: per-FACE world-axis box projection at the material's real-world
			// tile size (worldSize ÷ tilingScale) — correct physical scale, continuous, no manual
			// re-UV, no streaks on perpendicular faces.
			applyBoxProjectedUVs(node, bound.metresPerTile, { withUv1: bound.aoMap });
			node.material = bound.material;
			mapped++;
		} else {
			// Unbound slot (or a binding whose material failed to load) — keep the authored GLB
			// material rather than a procedural placeholder (Step 6). Don't count as re-skinned.
			return;
		}
		swapped++;
	});

	const brightness = clampBrightness(
		knobs.brightness != null ? knobs.brightness : defaults.brightness
	);

	let lightingHandled = false;
	let lightsAdded = [];
	if (resolved.light?.lighting) {
		// M7 Chunk 3 / WS4 — per-zone lights: shell LIGHT_ZONE_<name> markers + the
		// lighting-recipe index (for zoneLight.<name> mood overrides).
		// Env IBL resolves per the ratified rule ${SCENE_CDN}/assets/${shopId}/${sha}/${asset};
		// shopId/sha come from the shop GLB's assetKey ("shopId/sha/file").
		const shopAssetKey = projectAssets?.shop?.assetKey || "";
		const [envShopId, envSha] = shopAssetKey.split("/");
		const zoneMarkers = extractLightZoneMarkers(shellRoot);
		if (stage) stage._lightZoneMarkers = zoneMarkers;   // Studio reads these to highlight the zone being edited (WS4)
		lightsAdded = applyLightingRecipe(stage, resolved.light, brightness, {
			knobs,
			zoneMarkers,
			recipesById: indexLightingRecipes(presetsForTemplate),
			shopId: envShopId || stage?.currentShopId || null,
			librarySha: envSha || stage?._librarySha || null,   // test-shop has no shell assetKey → fall back to the boot mint sha
			templatesSha: stage?._templatesSha || null,          // ASK-124 B3 — the shared per-pin env base
		});
		lightingHandled = true;
	}

	// Signage: scan the shell for SIGN_ANCHOR_NN empties and build a sign
	// mesh per anchor based on the signMode knob. Each built mesh is
	// tracked so recomposeTemplate can clear them on hot-swap.
	// Sign tint comes from the template-wide accentColor knob (brand glow)
	// — *not* from a per-sign override. Compat fallback honours the older
	// knobs.signMode.tint shape for saved-state migration.
	const accentColor =
		(typeof knobs.accentColor === "string" && knobs.accentColor) ||
		knobs.signMode?.tint ||
		defaults.accentColor;
	const signPreset = resolvePreset(presetsForTemplate, "sign-mode", knobs.signMode, defaults.signMode);
	const signsAdded = buildSigns({
		shellRoot, scene, stage,
		signPreset,
		tintHex: accentColor,
		projectAssets,
		commerce,
		shopTitle,            // ASK-129 F5 — the boot's own shops[id].title, last resort before "Shop"
		foldedKnobs: knobs,   // shop-knob overrides (incl. shopName) already folded
	});

	console.log(
		`[template] composed '${templateId}' — ` +
		`walls=${resolved.wall?.presetId || "?"}${knobs.wallMaterial?.tint    ? `(tint=${knobs.wallMaterial.tint})`    : ""} ` +
		`floor=${resolved.floor?.presetId || "?"}${knobs.floorMaterial?.tint  ? `(tint=${knobs.floorMaterial.tint})`  : ""} ` +
		`ceiling=${resolved.ceiling?.presetId || "?"}${knobs.ceilingTreatment?.tint ? `(tint=${knobs.ceilingTreatment.tint})` : ""} ` +
		`fixtures=${resolved.fixture?.presetId || "?"}${knobs.fixtureMaterial?.tint ? `(tint=${knobs.fixtureMaterial.tint})` : ""} ` +
		`counter=${resolved.counter?.presetId || "?"}${knobs.counterMaterial?.tint ? `(tint=${knobs.counterMaterial.tint})` : ""} ` +
		`light=${resolved.light?.presetId || "?"} ` +
		`sign=${signPreset?.presetId || "?"} accent=${accentColor} brightness=${brightness.toFixed(2)} ` +
		`(${swapped} meshes re-skinned — ${mapped} via loose-KTX2 library material${mapped ? ` [${Object.keys(boundBySlot).join("+")}]` : ""}, ${signsAdded.length} signs)`
	);

	if (diagnoseEnabled()) {
		// `templatePresets` joins the payload for row 6's second swatch source. It is passed
		// EXPLICITLY rather than read from the enclosing scope because this function is a sibling of
		// composeTemplate, not a closure inside it — reading it there is a ReferenceError that fires
		// only under `?diagnose=1`, which no build step and no typecheck would have seen.
		void emitPristineDiagnose({
			scene, shellRoot, templateId, presetsForTemplate, knobs, defaults,
			resolved, projectAssets, templateCatalogue, templatePresets, swapped,
		});
	}

	return { composed: true, lightingHandled, lightsAdded, signsAdded };
}

// Gather the live future-vs-fallback facts for the active shop and log the pristine
// report (ASK-080 Track-1). Pure-ish: only reads, then console.logs. Errors here must
// never break composition, so the whole thing is best-effort — including the import, which is now
// dynamic: a failed chunk fetch costs the diagnostic, never the compose. `async` for the same
// reason the caller does not await it; the report is a log line, not a step composition depends on.
async function emitPristineDiagnose({ scene, shellRoot, templateId, presetsForTemplate, knobs, defaults, resolved, projectAssets, templateCatalogue, templatePresets, swapped }) {
	try {
		const { evaluatePristine, formatPristineReport } = await import("./_pristine-diagnostics.js");
		// 2 — which preset categories fell back to their default (requested id not in library).
		const PRESET_CATS = [
			["wall-material", "wallMaterial"], ["floor-material", "floorMaterial"],
			["ceiling-treatment", "ceilingTreatment"], ["fixture-material", "fixtureMaterial"],
			["counter-material", "counterMaterial"], ["lighting-recipe", "lightingRecipe"],
			["sign-mode", "signMode"],
		];
		const presetFallbacks = [];
		for (const [category, knobKey] of PRESET_CATS) {
			const requestedId = knobs[knobKey]?.preset || defaults[knobKey];
			if (!requestedId) continue;
			if (!presetsForTemplate?.[category]?.[requestedId]) presetFallbacks.push(`${category}=${requestedId}`);
		}

		// 4 — IBL env source (recipe vs hub fallback vs none).
		const envSource = resolveEnvironment(resolved.light, { fallbackAsset: HUB_FALLBACK_HDRI }).source;

		// 5 — per-slot asset resolution path.
		const assetSlots = Object.keys(projectAssets || {}).map((slot) => {
			const e = projectAssets[slot] || {};
			return { slot, source: e.assetKey ? "assetKey" : (e.assetUrl ? "assetUrl" : "missing") };
		});

		// 6 — swatch source ("unknown" when neither source is threaded through).
		//
		// ⚠️ THE PRECEDENCE HERE MUST MATCH `studio/_palette.js` resolveSwatchesWithSource(), and it is
		// duplicated rather than imported because that module lives under `studio/` — importing it into
		// this customer-path file would leak editor code into the Play entry, which check-bundle-budget
		// gates by name. `test-palette` asserts the two vocabularies and their order agree, so the
		// duplication cannot drift silently.
		//
		// ASK-129 §knob-husk added the presets source: a COMPOSED SHELL is deliberately absent from
		// templateCatalogue (D-068), so before this it reported `default` — "the client fallback is
		// showing" — for a shop that would in fact be showing Content's own palette. A diagnostic that
		// under-reports a fallback is worse than no row, because it is read as evidence.
		const hasSwatches = (v) => Array.isArray(v?.paletteSwatches?.swatches) && v.paletteSwatches.swatches.length > 0;
		const presetsBlock = templatePresets?.[templateId] ?? null;
		let swatchesSource = "unknown";
		if (Array.isArray(templateCatalogue) || presetsBlock) {
			const entry = Array.isArray(templateCatalogue) ? templateCatalogue.find((t) => t && t.templateId === templateId) : null;
			swatchesSource = hasSwatches(entry) ? "catalogue" : hasSwatches(presetsBlock) ? "presets" : "default";
		}

		// 9 — recipe zones vs shell markers.
		const recipeZones = Object.keys(resolved.light?.zones || {}).filter((z) => z !== "default").map((z) => z.toLowerCase());
		const markers = Object.keys(extractLightZoneMarkers(shellRoot)).map((z) => z.toLowerCase());
		const missingZones = recipeZones.filter((z) => !markers.includes(z));

		const report = evaluatePristine({
			shopId: (projectAssets?.shop?.assetKey || "").split("/")[0] || scene?.id || null,
			templateId,
			templateBound: !!presetsForTemplate && !!templateId,
			presetFallbacks,
			lightingV2: !!resolved.light?.lighting,
			envSource,
			assetSlots,
			swatchesSource,
			surfaceRegions: Array.isArray(scene?.surfaceRegions) ? scene.surfaceRegions.length : 0,
			swapped,
			recipeZones,
			missingZones,
		});
		const lines = formatPristineReport(report);
		const log = report.pristine ? console.log : console.warn;
		log(lines.join("\n"));
	} catch (err) {
		console.warn("[diagnose] pristine report failed (non-fatal):", err);
	}
}

// ─── Signage ────────────────────────────────────────────────────────────────

// Scans shellRoot for SIGN_ANCHOR_NN empties and builds a sign per anchor
// according to the chosen mode. Returns the list of added meshes so the
// hot-swap path can clear them. Anchor convention (matches LONGBOX et al):
//   - position = sign centre in world
//   - rotation Z (Blender) → rotation Y (Three) defines local +X = outward normal
//   - scale: Blender (thickness, width, height) → Three (X=thin, Y=height, Z=width)
function buildSigns({ shellRoot, scene, stage, signPreset, tintHex, projectAssets, commerce, shopTitle, foldedKnobs }) {
	const added = [];
	if (!signPreset) return added;
	// ASK-050 shopName resolution. The merchant's Studio knob wins; otherwise
	// the CANONICAL merchant identity — commerce.storeName, the same source as
	// _resolveSellerOfRecord (DECISION-030) — so the sign on the wall and the
	// seller-of-record disclosure can never disagree about who the merchant is.
	// Scene semantic/metadata titles are authoring labels, kept as last resorts.
	// 🔴 ASK-129 §E2E102 F5 — `shopTitle` (the boot's own `shops[id].title`) joins the chain ahead of
	// the "Shop" literal. Measured on the plane for the ladder shop: knobs.shopName undefined,
	// commerce `{}`, semantic.name undefined, metadata.title undefined — every source empty, so the
	// sign fell through to the bare word "Shop", which is what Vanessa reported. The boot title was
	// sitting one field away the whole time, and it is the SAME source §vacant-sign resolved the hub
	// door's sign from: one shop name, rendered on both sides of the door.
	const shopName =
		foldedKnobs?.shopName ||
		scene?.template?.knobs?.shopName ||
		commerce?.storeName ||
		scene?.semantic?.name ||
		scene?.metadata?.title ||
		shopTitle ||
		"Shop";

	// Uploaded-logo image source.
	//
	// 🔴 ASK-129 §E2E102-rerun item 1 — THE MERCHANT'S OWN UPLOAD GOES FIRST, and its absence here is
	// the defect behind "the image does not show as signage". The Studio's logo upload writes
	// `knobs.signLogoUrl`; the sign builder read only the AUTHORED asset slots and never that knob,
	// so a merchant could upload a logo, have every link of the chain go green — sign, storage PUT,
	// finalize, knob, rebake, boot, object live at 200 image/jpeg — and still get a sign built from
	// an image they had nothing to do with, or from none at all. Flipping signMode alone (the first
	// half of this item) only got as far as "logo mode, no logo": measured, the sign's pixels moved
	// 4.9 against a threshold of 5, because the mode changed and the picture did not.
	//
	// Authored slots stay as the fallback, so a shop that ships a brand image still gets one before
	// the merchant uploads anything.
	const uploadedLogoUrl =
		foldedKnobs?.signLogoUrl ||
		projectAssets?.shopLogo?.url ||
		projectAssets?.spinnerRack?.headerLogoUrl ||
		null;

	// DECISION-053 Slice-2 — stash the resolved shop-BRAND signage (merchant logo + name + the
	// shop-level signMode) so the fixture signage path (spinner header, in the composer) sources
	// from the SAME brand + mode as SIGN_ANCHOR, not a fixture-local asset. signMode drives the
	// source (typographic-shopname → shop-name text; uploaded-logo → logo); name is the never-blank
	// fallback when a logo mode has no logo.
	if (stage) stage._shopBrandSignage = { logoUrl: uploadedLogoUrl, name: shopName, signMode: signPreset?.presetId || null };

	shellRoot.traverse((node) => {
		if (!/^SIGN_ANCHOR(?:_\d+)?$/i.test(node.name || "")) return;
		node.updateMatrixWorld(true);
		const worldPos   = new THREE.Vector3();
		const worldQuat  = new THREE.Quaternion();
		const worldScale = new THREE.Vector3();
		node.matrixWorld.decompose(worldPos, worldQuat, worldScale);

		// Per-anchor override: an anchor empty can carry a `text` extra in the
		// shell GLB to label a zone (e.g. "Comic Boxes" over the east wall).
		// Falls back to the shop name for the canonical entrance sign.
		const signText = node.userData?.text || shopName;

		let mesh = null;
		switch (signPreset.presetId) {
			case "typographic-shopname":
				mesh = buildTypographicSign({ shopName: signText, tintHex, scale: worldScale });
				break;
			case "uploaded-logo":
				mesh = buildUploadedLogoSign({ url: uploadedLogoUrl, scale: worldScale, stage, name: signText, tintHex });
				break;
			default:
				return;
		}
		if (!mesh) return;

		mesh.position.copy(worldPos);
		// Plane default normal = +Z. We want normal = anchor's local +X (outward).
		// Plane.q = anchor.q * R_y(+π/2) maps plane local +Z → anchor local +X. See
		// derivation in DEVLOG entry for sign mode.
		const align = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
		mesh.quaternion.copy(worldQuat).multiply(align);
		// Nudge a hair off the wall to avoid z-fighting with the wall surface.
		const outward = new THREE.Vector3(1, 0, 0).applyQuaternion(worldQuat).normalize();
		mesh.position.addScaledVector(outward, 0.01);

		stage.scene.add(mesh);
		stage.shopLights.push(mesh);
		added.push(mesh);
	});

	return added;
}

const SIGN_TEX_CACHE = new Map(); // key → THREE.CanvasTexture

function buildTypographicSign({ shopName, tintHex, scale }) {
	const widthM  = Math.max(0.2, scale.z);
	const heightM = Math.max(0.1, scale.y);
	const aspect  = widthM / heightM;

	const cacheKey = `${shopName}|${tintHex || ""}|${aspect.toFixed(3)}`;
	let texture = SIGN_TEX_CACHE.get(cacheKey);
	if (!texture) {
		const canvasH = 256;
		const canvasW = Math.max(256, Math.round(canvasH * aspect));
		const canvas = document.createElement("canvas");
		canvas.width = canvasW;
		canvas.height = canvasH;
		const ctx = canvas.getContext("2d");
		drawNeonText(ctx, canvasW, canvasH, shopName, tintHex || "#fff2c0");
		texture = new THREE.CanvasTexture(canvas);
		texture.colorSpace = THREE.SRGBColorSpace;
		texture.anisotropy = 4;
		texture.needsUpdate = true;
		SIGN_TEX_CACHE.set(cacheKey, texture);
	}

	const tintColor = new THREE.Color(tintHex || "#fff2c0");
	const mat = new THREE.MeshStandardMaterial({
		color:             0x000000,
		map:               texture,
		emissive:          tintColor,
		emissiveMap:       texture,
		emissiveIntensity: 1.4,
		toneMapped:        false,
		transparent:       true,
		side:              THREE.FrontSide,
	});

	const geo = new THREE.PlaneGeometry(widthM, heightM);
	return new THREE.Mesh(geo, mat);
}

// Uploaded-logo sign: render an image fitted onto a canvas that matches
// the sign plane's aspect ratio. Letterboxing preserves the logo's
// aspect — no distortion when the sign plane and logo image disagree.
// Texture is built immediately as a transparent canvas; the image draws
// in async after textureLoader resolves, then we flag needsUpdate.
const UPLOADED_LOGO_TEX_CACHE = new Map();
function buildUploadedLogoSign({ url, scale, stage, name, tintHex }) {
	if (!url) {
		// No logo to show and logo mode selected → the NAME, not nothing. Returning null here left the
		// anchor with no mesh at all, which is the same blank wall by a different route.
		console.warn("[sign] uploaded-logo: no logo url (knob or authored slot) — falling back to the name sign");
		return buildTypographicSign({ shopName: name || "Shop", tintHex, scale });
	}
	const widthM  = Math.max(0.2, scale.z);
	const heightM = Math.max(0.1, scale.y);
	const aspect  = widthM / heightM;

	const cacheKey = `${url}|${aspect.toFixed(3)}`;
	let tex = UPLOADED_LOGO_TEX_CACHE.get(cacheKey);
	let canvas;
	if (!tex) {
		const canvasH = 256;
		const canvasW = Math.max(256, Math.round(canvasH * aspect));
		canvas = document.createElement("canvas");
		canvas.width = canvasW;
		canvas.height = canvasH;
		const ctx = canvas.getContext("2d");
		ctx.clearRect(0, 0, canvasW, canvasH);

		tex = new THREE.CanvasTexture(canvas);
		tex.colorSpace = THREE.SRGBColorSpace;
		tex.anisotropy = 4;
		tex.needsUpdate = true;
		UPLOADED_LOGO_TEX_CACHE.set(cacheKey, tex);

		// 🔴 THE SIGN IS NEVER BLANK. Draw the shop's NAME first, synchronously, then replace it with
		// the logo when the image arrives. The canvas used to start transparent and stay that way
		// until an async callback drew on it — so between the two, and for ever if the load never
		// completed, the merchant got an INVISIBLE mesh on a bare wall. That is what
		// "the image does not show as signage" looked like from the shop floor, and it is worse than
		// a wrong sign because there is nothing to report. The code above this already promised it:
		// "name is the never-blank fallback when a logo mode has no logo".
		drawNeonText(ctx, canvasW, canvasH, name || "Shop", tintHex);
		tex.needsUpdate = true;

		// 🔴 AND THE IMAGE IS LOADED WITH A PLAIN `Image`, NOT `stage.textureLoader`. Measured on the
		// deployed bundle: the loader's callback never ran — the canvas sampled [0,0,0,0] at its
		// centre 15s after an upload whose object fetches 200 image/png and which `new Image()` in the
		// same page loads happily at 64×64. We own this canvas, we need the raw bitmap and not a
		// second THREE.Texture, and the shared loader's manager is a dependency this had no use for.
		// It also gets what it never had: an ERROR PATH.
		const img = new Image();
		img.crossOrigin = "anonymous";
		img.onload = () => {
			if (!img.width || !img.height) return;
			const ctxAsync = canvas.getContext("2d");
			ctxAsync.clearRect(0, 0, canvasW, canvasH);
			const imgAspect = img.width / img.height;
			let drawW, drawH;
			if (imgAspect > aspect) {
				// Image is wider than the canvas — fit by width, letterbox top/bottom
				drawW = canvasW;
				drawH = canvasW / imgAspect;
			} else {
				// Image is taller — fit by height, letterbox sides
				drawH = canvasH;
				drawW = canvasH * imgAspect;
			}
			ctxAsync.drawImage(img, (canvasW - drawW) / 2, (canvasH - drawH) / 2, drawW, drawH);
			tex.needsUpdate = true;
		};
		img.onerror = () => {
			// The name is already on the canvas, so the shop keeps a readable sign. Say why, once,
			// with the URL — a silent failure here is a blank wall nobody can explain.
			console.warn(`[sign] uploaded-logo: could not load ${url} — keeping the typographic name sign`);
		};
		img.src = url;
	}

	const mat = new THREE.MeshStandardMaterial({
		color:             0xffffff,
		map:               tex,
		emissive:          0xffffff,        // self-illuminated, so it reads even in dim lighting
		emissiveMap:       tex,
		emissiveIntensity: 0.6,
		toneMapped:        false,
		transparent:       true,
		side:              THREE.FrontSide,
	});

	const geo = new THREE.PlaneGeometry(widthM, heightM);
	return new THREE.Mesh(geo, mat);
}

function drawNeonText(ctx, w, h, text, glowHex) {
	ctx.clearRect(0, 0, w, h);
	// Transparent base — only the text glows.
	const glow = glowHex || "#fff2c0";
	// Auto-size font to fill the canvas
	let fontSize = Math.floor(h * 0.62);
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";
	ctx.font = `bold ${fontSize}px "Helvetica Neue", "Arial Black", sans-serif`;
	let measured = ctx.measureText(text).width;
	const maxWidth = w * 0.88;
	if (measured > maxWidth) {
		fontSize = Math.floor(fontSize * (maxWidth / measured));
		ctx.font = `bold ${fontSize}px "Helvetica Neue", "Arial Black", sans-serif`;
	}
	// Halo passes — multiple shadow renders build a soft neon bloom.
	ctx.shadowColor = glow;
	for (const radius of [28, 18, 10, 4]) {
		ctx.shadowBlur = radius;
		ctx.fillStyle = glow;
		ctx.fillText(text, w / 2, h / 2);
	}
	// Inner core white-hot
	ctx.shadowBlur = 2;
	ctx.fillStyle = "#ffffff";
	ctx.fillText(text, w / 2, h / 2);
}

// ─── Internals ──────────────────────────────────────────────────────────────

function resolvePreset(library, category, knobValue, defaultId) {
	// Accept both knob spellings: authored object {preset, tint} AND the bare preset-id string
	// (the /knobs/shop wire form — Coord's raw-knob sweep used it and silently got the default).
	const id = knobValue?.preset || (typeof knobValue === "string" && knobValue) || defaultId;
	if (!id) return null;
	const preset = library?.[category]?.[id];
	if (!preset) {
		console.warn(`[template] preset not found: ${category}/${id}; falling back to default '${defaultId}'`);
		return library?.[category]?.[defaultId] || null;
	}
	return preset;
}





const IBL_FILL_SCALE = 0.45;   // glTF has no ambient — IBL provides indirect; cut the fake fill so the shop isn't over-lit (Khronos note, ASK-080 §A)

/** The env HDRI request for a lighting recipe — ONE resolver for the early request and the recipe's own. */
function envRequestFor(preset, templatesSha) {
	const env = resolveEnvironment(preset);
	return {
		envUrls: envAssetCandidates({ templatesSha, asset: env.asset }),
		lodUrls: envAssetCandidates({ templatesSha, asset: env.assetLod }),
		envFallbackUrl: resolveEnvAssetUrl(`projects/galleria-hub/assets/${HUB_FALLBACK_HDRI}`),
	};
}

function applyLightingRecipe(stage, preset, brightness = 1.0, opts = {}) {
	const cfg = preset.lighting;
	if (!cfg) return [];

	const scene    = stage.scene;
	const renderer = stage.renderer;
	const b        = brightness;
	// Track lights this recipe authors so the Studio hot-swap path can
	// surgically remove them without touching products / fixtures that
	// also live in stage.shopLights.
	const added = [];
	const trackedPush = (obj) => { scene.add(obj); stage.shopLights.push(obj); added.push(obj); };

	const tone = cfg.tone || {};
	if (tone.background) scene.background = new THREE.Color(tone.background);
	if (tone.fog?.color != null && tone.fog?.density != null) {
		scene.fog = new THREE.FogExp2(new THREE.Color(tone.fog.color), tone.fog.density);
	} else {
		scene.fog = null;
	}
	// M7 Chunk 3 / WS4 (ASK-080 §A) — IBL: load the recipe's HDRI → scene.environment
	// (was null = under-spec rendering of glTF metals). preset carries v2 environment
	// {asset (2K), assetLod (1K)}; LOD loads first for fast paint then swaps to 2K.
	// Async + graceful: keeps no env if the asset is absent / CDN unset (bundled dev).
	const env = resolveEnvironment(preset);
	// ASK-099 / D-059 item 1 — recipe env HDRIs live in the shop's MINTED PACKAGE on the Scene CDN:
	// `${CDN}/${shopId}/${librarySha}/env/${asset}` (verified 200 image/vnd.radiance) — the SAME
	// per-shop base as other minted assets. shopId+sha come from the shell assetKey ("shopId/sha/file")
	// for shops that carry one (demo-store); test-shop's shell ref is the SHARED template-registry copy
	// with NO assetKey, so shopId/sha fell to null → HDRI unresolved → dark. Fallback = currentShopId +
	// the boot MINT sha (stage._librarySha, from generatedFrom's @sha — == the shell assetKey sha), set
	// per scene by the app. Last resort = the bundled hub HDRI (runtime origin) so a shop is never dark
	// if its curated HDRI is missing. applyShopEnvironment PRE-FLIGHTS content-type (never trusts a 200:
	// the origin SPA-fallback returns 200 text/html) then bakes recipe-then-fallback.
	// ASK-124 B3 — CLOSED 2026-08-30. The recipe HDRI belongs to the template FAMILY, not to
	// the shop, so it resolves ONLY against the shared per-pin key now; the per-shop leg is
	// deleted (it served 0 shops and 404'd on every copy). `templatesSha` is the pin the
	// recipe itself came from (boot.templatesSha). shopId/librarySha are still threaded
	// through `opts` for the OTHER asset kinds above — they are simply no longer env inputs.
	const { envUrls, lodUrls, envFallbackUrl } = envRequestFor(preset, opts.templatesSha);
	const iblActive = !!((envUrls.length || envFallbackUrl) && typeof stage.applyShopEnvironment === "function");
	if (iblActive) {
		void stage.applyShopEnvironment(envUrls, lodUrls, envFallbackUrl);
	} else {
		scene.environment = null;
	}
	// When IBL is active it supplies the indirect light — scale the non-physical
	// ambient/fill down so metals + walls aren't blown out (Khronos: IBL replaces fill).
	const fillScale = iblActive ? IBL_FILL_SCALE : 1;
	// Exposure stays at the recipe's value — brightness only scales light
	// intensities so the merchant's slider behaves linearly without
	// stacking with tone mapping.
	if (typeof tone.exposure === "number") renderer.toneMappingExposure = tone.exposure;

	if (cfg.ambient && stage.ambientLight) {
		stage.ambientLight.color.set(cfg.ambient.color || "#ffffff");
		stage.ambientLight.intensity = (cfg.ambient.intensity ?? 1.0) * b * fillScale;
	}
	if (cfg.fill && stage.fillLight) {
		stage.fillLight.color.set(cfg.fill.color || "#ffffff");
		stage.fillLight.intensity = (cfg.fill.intensity ?? 0.5) * b * fillScale;
		stage.fillLight.castShadow = false;
	}

	// Floor plane hidden — shell GLB provides its own floor.
	if (stage.floor) stage.floor.visible = false;

	if (cfg.hemi) {
		const hemi = new THREE.HemisphereLight(
			new THREE.Color(cfg.hemi.skyColor || "#ffffff"),
			new THREE.Color(cfg.hemi.groundColor || "#000000"),
			(cfg.hemi.intensity ?? 0.6) * b
		);
		hemi.position.set(0, 4, 0);
		trackedPush(hemi);
	}

	for (const s of (cfg.spots || [])) {
		const spot = new THREE.SpotLight(
			new THREE.Color(s.color || "#ffffff"),
			(s.intensity ?? 4.0) * b,
			s.distance ?? 14,
			s.angle ?? 1.0,
			s.penumbra ?? 0.5,
			s.decay ?? 1
		);
		if (Array.isArray(s.position)) spot.position.set(...s.position);
		if (Array.isArray(s.target))   spot.target.position.set(...s.target);
		spot.castShadow = false;
		trackedPush(spot);
		trackedPush(spot.target);
	}

	for (const p of (cfg.points || [])) {
		const pt = new THREE.PointLight(
			new THREE.Color(p.color || "#ffffff"),
			(p.intensity ?? 1.5) * b,
			p.distance ?? 8,
			p.decay ?? 1.5
		);
		if (Array.isArray(p.position)) pt.position.set(...p.position);
		trackedPush(pt);
	}

	// M7 Chunk 3 / WS4 — per-zone lights: place each recipe zone's key/fill at its
	// LIGHT_ZONE_<name> marker (resolveZonePlan honours zoneLight.<name> overrides).
	// The whole-shop "default" zone has no marker — its mood is the global ambient/fill
	// above. No-op until Content ships the markers (zones = []).
	const zones = resolveZonePlan(preset, opts.knobs || {}, opts.zoneMarkers || {}, opts.recipesById || {});
	for (const z of zones) {
		if (!z.position) continue;
		if (z.key) {
			// The zone KEY is authored as a downward SPOTLIGHT (color/intensity + angle/
			// penumbra/aim:"down"). Render it as a SpotLight, NOT a PointLight: a PointLight
			// discards angle/penumbra/aim and radiates omnidirectionally, which both washes
			// the accent out under the IBL and makes mood-switching nearly invisible (recipes
			// differ mostly in cone shape). A focused cone reads as a real per-zone accent.
			// Markers sit at ceiling height (~2.4–2.6m); aim straight down to the floor.
			const k = new THREE.SpotLight(
				new THREE.Color(z.key.color || "#ffffff"),
				(z.key.intensity ?? 4.0) * b,
				z.key.distance ?? 10,
				z.key.angle ?? 0.6,
				z.key.penumbra ?? 0.4,
				z.key.decay ?? 1.3,
			);
			k.position.set(z.position.x, z.position.y, z.position.z);
			k.target.position.set(z.position.x, 0, z.position.z);   // aim:"down"
			k.castShadow = false;   // the single contact-shadow caster handles grounding
			trackedPush(k);
			trackedPush(k.target);
		}
		if (z.fill) {
			// Fill is authored as an omni soft wash (color + intensity only) → PointLight.
			const f = new THREE.PointLight(new THREE.Color(z.fill.color || "#ffffff"), (z.fill.intensity ?? 0.6) * b, z.fill.distance ?? 6, z.fill.decay ?? 1.5);
			f.position.set(z.position.x, z.position.y, z.position.z);
			trackedPush(f);
		}
	}

	// ASK-084 Part 2 — the two lighting models behind `shadows.model` (a tiered STACK, not either/or).
	// The tier picks the default (high→A, else B); ?model= / recipe shadows.model override for A/B.
	// All shadow SETUP is owned by the framework standard (stage._configureShadows): fit-to-bounds +
	// tier res/filter (VSM) + real-surface receivers + catcher retire. No hardcoded frustum/catcher.
	const shadows = resolveShadows(preset);
	if (shadows.enabled && renderer && stage) {
		const model = resolveShadowModel({ override: stage.shadowModelOverride, recipeModel: shadows.model, tier: stage.renderTier });
		const shadowIntensity = resolveShadowIntensity(opts.knobs?.shadowIntensity);   // undefined → SHADOW_INTENSITY_DEFAULT (2.5)
		const casters = [];
		if (model === "A") {
			// Model A — dedicated DirectionalLight sun/key. Crisp directional form; IBL supplies
			// indirect. Directional-ONLY — no contact caster. Owner fits it (angled ortho). The
			// shadowIntensity knob scales its shadow DARKNESS (LightShadow.intensity) in the owner.
			const sun = new THREE.DirectionalLight(0xfff4e6, 2.0 * b);
			sun.target.position.set(0, 0, 0);
			trackedPush(sun);
			trackedPush(sun.target);
			casters.push(sun);
		} else {
			// Model B — contact overhead spot. REAL intensity = shadowIntensity so it casts a visible
			// shadow on the real floor/fixtures (Part 1 retired the ShadowMaterial catcher, so a
			// ~zero-intensity caster grounds nothing). Rebalance ambient down so overall brightness
			// stays where the brightness knob set it — the shadow reads as contrast, not extra light.
			const caster = new THREE.SpotLight(0xffffff, shadowIntensity, 40, Math.PI / 3, 0.6, 1);
			caster.target.position.set(0, 0, 0);
			trackedPush(caster);
			trackedPush(caster.target);
			casters.push(caster);
			if (stage.ambientLight) stage.ambientLight.intensity *= ambientRebalanceForShadow(shadowIntensity);
		}
		stage._configureShadows({ mode: "recipe", casters, model, shadowIntensity });
		console.log(`[shadows] recipe model=${model} shadowIntensity=${shadowIntensity} (tier=${stage.renderTier}${stage.shadowModelOverride ? `, ?model=${stage.shadowModelOverride}` : ""})`);
	}

	return added;
}

// Shell LIGHT_ZONE_<name> empties → { <name>: {x,y,z} } world positions (WS4 per-zone).
function extractLightZoneMarkers(shellRoot) {
	const out = {};
	if (!shellRoot || typeof shellRoot.traverse !== "function") return out;
	const v = new THREE.Vector3();
	shellRoot.traverse((n) => {
		const m = n.name && /^LIGHT_ZONE_(.+)$/i.exec(n.name);
		if (m) { n.getWorldPosition(v); out[m[1].toLowerCase()] = { x: v.x, y: v.y, z: v.z }; }
	});
	return out;
}

// lighting-recipe presets → { presetId: recipe } for zoneLight.<name> mood overrides.
function indexLightingRecipes(presets) {
	const out = {};
	const list = Array.isArray(presets) ? presets : (presets ? Object.values(presets) : []);
	for (const p of list) { if (p && p.category === "lighting-recipe" && p.presetId) out[p.presetId] = p; }
	return out;
}

// ─── Procedural textures ────────────────────────────────────────────────────







